/**
 * ThemeV2Service
 *
 * Manages ThemeV2 entities at root Firestore collection level.
 * V2 themes are stored separately from V1 in themesV2/{themeId}.
 *
 * Operations:
 * - create: Create new V2 theme with generated ID
 * - get: Get V2 theme by ID (returns null if not found)
 * - list: List V2 themes ordered by updatedAt desc (with pagination)
 * - update: Update V2 theme using set with merge (NEVER updateDoc)
 * - delete: Delete V2 theme (always allowed)
 * - clone: Deep copy V2 theme with new name and timestamps
 *
 * NOTE: No real-time subscriptions needed (admin CRUD feature)
 *
 * @module firebase-core/services
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  startAfter,
  FirestoreDataConverter,
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import { themeV2Converter } from '../converters/themeV2Converter';
import { mergeWriteReplacingFields } from '@modules/firebase-core/utils/mergeWriteReplacingFields';
import { stripUndefined } from '@modules/firebase-core/utils/stripUndefined';
import type { ThemeV2, CreateThemeV2Input, UpdateThemeV2Input } from '@modules/themes/types/themesV2.types';

/**
 * Service for ThemeV2 CRUD operations.
 *
 * V2 themes are stored at root level: themesV2/{themeId}
 * Completely separate from V1 themes (themes/{themeId}).
 */
export class ThemeV2Service {
  private readonly collectionName = 'themesV2';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<ThemeV2>;

  constructor(converter: FirestoreDataConverter<ThemeV2> = themeV2Converter) {
    this.converter = converter;
  }

  // ============================================================================
  // CREATE
  // ============================================================================

  /**
   * Create a new V2 theme with generated ID.
   *
   * @param data - Partial theme data (id, createdAt, updatedAt auto-generated)
   * @returns Created theme with all fields populated
   */
  async create(data: CreateThemeV2Input): Promise<ThemeV2> {
    try {
      const collectionRef = collection(this.firestore, this.collectionName);
      const docRef = doc(collectionRef).withConverter(this.converter);
      const now = new Date().toISOString();

      const themeData: ThemeV2 = {
        ...data,
        id: docRef.id,
        createdAt: now,
        updatedAt: now,
      } as ThemeV2;

      await setDoc(docRef, themeData);
      return themeData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ThemeV2Service.create');
      throw error;
    }
  }

  // ============================================================================
  // READ
  // ============================================================================

  /**
   * Get V2 theme by ID.
   *
   * @param themeId - Theme document ID
   * @returns ThemeV2 if found, null otherwise
   */
  async get(themeId: string): Promise<ThemeV2 | null> {
    try {
      const docRef = doc(this.firestore, this.collectionName, themeId).withConverter(
        this.converter
      );
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { ...docSnap.data(), id: docSnap.id } as ThemeV2;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ThemeV2Service.get');
      throw error;
    }
  }

  /**
   * List V2 themes ordered by updatedAt descending.
   *
   * @param options - Optional pagination: limit, startAfter (ISO date string)
   * @returns Array of V2 themes
   */
  async list(options?: { limit?: number; startAfter?: string }): Promise<ThemeV2[]> {
    try {
      const collectionRef = collection(this.firestore, this.collectionName).withConverter(
        this.converter
      );
      let q = query(collectionRef, orderBy('updatedAt', 'desc'));

      if (options?.limit) {
        q = query(q, limit(options.limit));
      }

      if (options?.startAfter) {
        q = query(q, startAfter(options.startAfter));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ThemeV2Service.list');
      throw error;
    }
  }

  // ============================================================================
  // UPDATE
  // ============================================================================

  /**
   * Update V2 theme fields.
   *
   * Fields are deep-merged (`merge: true`), except `animationSettings` and `flowDocument`:
   * whole-value snapshots, replaced as a whole when present (`mergeFields`), in the same
   * atomic batch. Both writes are deep-stripped of `undefined` first (`prepareData:
   * stripUndefined`) — the converter only strips TOP-LEVEL `undefined`, and a nested one
   * (e.g. a directional transition's `direction` reset to "Default", which `structuredClone`
   * keeps as an own key) would otherwise reach the SDK unstripped and throw synchronously,
   * before `commit()` — nothing saved, not even the unrelated fields in the same batch
   * (interim review I-1).
   *
   * CRITICAL: Uses set with merge (NEVER updateDoc) to ensure converter runs.
   *
   * @param themeId - Theme document ID
   * @param data - Partial theme data to update
   */
  async update(themeId: string, data: UpdateThemeV2Input): Promise<void> {
    try {
      const docRef = doc(this.firestore, this.collectionName, themeId).withConverter(
        this.converter
      );
      const batch = CoreFirebaseService.getInstance().batch();

      // CRITICAL: Use set with merge, NEVER updateDoc. animationSettings and flowDocument
      // are whole-value snapshots: a deep merge would keep keys/screens the new value lacks
      // (Smooth's perKindOverrides after None; a scheme switch's deleted per-screen
      // screenAnimation inside the template flowDocument after None, AMV-AC-004 S3b), so
      // both are replaced wholesale instead. prepareData: stripUndefined deep-strips both
      // writes — the converter alone only strips top-level undefined (interim review I-1).
      mergeWriteReplacingFields(
        batch,
        docRef,
        { ...data, updatedAt: new Date().toISOString() } as Partial<ThemeV2>,
        ['animationSettings', 'flowDocument'],
        stripUndefined
      );
      await batch.commit();
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ThemeV2Service.update');
      throw error;
    }
  }

  // ============================================================================
  // DELETE
  // ============================================================================

  /**
   * Delete V2 theme by ID.
   *
   * Always allowed - themes are cloned to onboardings, so deletion
   * doesn't affect existing onboardings.
   *
   * @param themeId - Theme document ID
   */
  async delete(themeId: string): Promise<void> {
    try {
      const docRef = doc(this.firestore, this.collectionName, themeId);
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ThemeV2Service.delete');
      throw error;
    }
  }

  // ============================================================================
  // CLONE
  // ============================================================================

  /**
   * Clone V2 theme with new name.
   *
   * Creates deep copy of source theme with:
   * - New ID (auto-generated)
   * - New name (provided)
   * - New timestamps (createdAt, updatedAt)
   * - All other settings preserved (colors, fonts, optionsListSettings, flowDocument, etc.)
   *
   * @param themeId - Source theme ID to clone
   * @param newName - Name for the cloned theme
   * @returns Cloned theme
   * @throws Error if source theme not found
   */
  async clone(themeId: string, newName: string): Promise<ThemeV2> {
    try {
      // 1. Get source theme
      const sourceTheme = await this.get(themeId);
      if (!sourceTheme) {
        throw new Error(`Source V2 theme ${themeId} not found`);
      }

      // 2. Deep clone via JSON round-trip to handle nested objects (FlowDocument, etc.)
      const deepCloned = JSON.parse(JSON.stringify(sourceTheme));

      // 3. Create new theme with cloned data
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, createdAt, updatedAt, ...themeData } = deepCloned;

      const newTheme = await this.create({
        ...themeData,
        name: newName,
      });

      return newTheme;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ThemeV2Service.clone');
      throw error;
    }
  }
}
